import React, { useState, useEffect, useRef } from "react";
import Peer from "peerjs";
import { useLocation, useNavigate } from "react-router-dom";
import Orientation from "react-native-orientation-locker";
import UpArrow from "./images/up-arrow.png";
import Watermark from "./images/watermark_template.webp";
import downloadIcon from "./images/download.png";
import shareIcon from "./images/share.png";
import "../App.css";

// Helper hook to read query parameters.
function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const PhonePage = () => {
  const query = useQuery();
  const kioskId = query.get("kioskId");
  const navigate = useNavigate();

  // Validate kioskId.
  useEffect(() => {
    if (!kioskId) {
      console.error("No kiosk id provided");
      navigate("/phone");
    }
  }, [kioskId, navigate]);

  

  // View state: "phone" or "camera"
  const [currentView, setCurrentView] = useState("phone");
  // In camera view, phase is either "cameras" (preloaded streams visible) or "selfie" (selfie review)
  const [phase, setPhase] = useState("cameras");
  const [actionDone, setActionDone] = useState(false);
  // eslint-disable-next-line
  const [message, setMessage] = useState(null);

  // PeerJS and connection state.
  // eslint-disable-next-line
  const [peer, setPeer] = useState(null);
  // eslint-disable-next-line
  const [connection, setConnection] = useState(null);
  const [kioskStream, setKioskStream] = useState(null);
  const [showBlackScreen, setShowBlackScreen] = useState(false);
  const peerRef = useRef(null);
  const frontCamRef = useRef(null);
  const canvasRef = useRef(null);
  const preloadedContainerRef = useRef(null);

  // For capturing selfie on the kiosk side (already saved to localStorage).
  const [selfiePhoto, setSelfie] = useState(null);

  // loading bar
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const intervalId = setInterval(() => {
      setProgress((prev) => {
        if (prev < 99) {
          return prev + 1;
        } else {
          clearInterval(intervalId);
          return prev;
        }
      });
    }, 500);

    return () => clearInterval(intervalId);
  }, []);

  // Create a PeerJS instance for the phone.
  useEffect(() => {
    if (!kioskId) return; 
    const newPeer = new Peer(); 
    setPeer(newPeer);
    peerRef.current = newPeer;
    newPeer.on("open", (id) => {
      console.log("Phone Peer ID:", id);
      // Connect to the kiosk using kioskId.
      const conn = newPeer.connect(kioskId);
      conn.on("open", () => {
        console.log("Connected to kiosk!");
        setConnection(conn);
      });
      conn.on("data", (data) => {
        console.log("Received data from kiosk:", data);
        // When kiosk sends a ready message (with matching kioskId), switch to camera view.
        if (
          data &&
          typeof data === "object" &&
          data.status === "ready" &&
          data.kioskId === kioskId
        ) {
          setCurrentView("camera");
        }
      });
      conn.on("error", (err) => {
        console.error("Connection error:", err);
      });
    });

    // Listen for incoming calls to receive the kiosk's live stream.
    newPeer.on("call", (call) => {
      console.log("Received call from:", call.peer);
      if (call.peer !== kioskId) {
        console.error("Kiosk ID mismatch. Redirecting to /phone.");
        navigate("/phone");
        return;
      }
      // Answer the call without sending any local stream.
      call.answer();
      call.on("stream", (remoteStream) => {
        console.log("Received kiosk stream.");
        setKioskStream(remoteStream);
      });
    });

    return () => {
      newPeer.destroy();
    };
  }, [kioskId, navigate]);

  // Set the video source to the kiosk stream when it becomes available.
  useEffect(() => {
    if (frontCamRef.current && kioskStream) {
      frontCamRef.current.srcObject = kioskStream;
    }
  }, [kioskStream]);

  useEffect(() => {
    // Lock to portrait on component mount
    Orientation.lockToPortrait();

    // Unlock on component unmount
    return () => {
      Orientation.unlockAllOrientations();
    };
  }, []);

  // Timer sequence after an action is taken in selfie review.
  useEffect(() => {
    if (actionDone) {
      const messageTimer = setTimeout(() => {
        setShowBlackScreen(true);
        setMessage("I'll see you April 11.\nI'll know where you'll be.");
      }, 1);
      const redirectTimer = setTimeout(() => {
        window.location.href = "https://www.dropthemovie.com";
      }, 3000);
      return () => {
        clearTimeout(messageTimer);
        clearTimeout(redirectTimer);
      };
    }
  }, [actionDone]);

  useEffect(() => {
    if (currentView === "camera") {
      handleCapture();
      const timer = setTimeout(() => {
        setPhase("selfie");
        console.log("Captured selfie photo.");
      }, 20000);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line
  }, [currentView]);

  const captureSite = async (url) => {
    try {
      const targetUrl = url;
      const apiEndpoint = `https://api.apiflash.com/v1/urltoimage?access_key=${
        process.env.REACT_APP_API_KEY
      }&url=${encodeURIComponent(
        targetUrl
      )}&width=1920&height=1080&fresh=true&delay=5`;

      const response = await fetch(apiEndpoint);
      if (!response.ok) {
        throw new Error("Screenshot API request failed");
      }

      const blob = await response.blob();
      const imageUrl = URL.createObjectURL(blob);
      return imageUrl;
    } catch (error) {
      console.error("Error capturing external screenshot:", error);
    }
  };

  // automate and create a photobooth style selfie from the three video feeds
  const handleCapture = async () => {
    // Capture the two external camera feeds concurrently and wait for both to be done.
    const [cameraUrl] = await Promise.all([
      captureSite(
        "https://player.twitch.tv/?channel=dropthegrove&parent=example.com&muted=true&autoplay=true"
      ),
    ]);

    // Get the kiosk camera feed and convert it to a base64 image.
    const kioskCamData = await new Promise((resolve) => {
      const video = document.createElement("video");
      video.setAttribute("autoplay", "");
      video.setAttribute("muted", "");
      video.srcObject = kioskStream;
      video.onloadedmetadata = () => {
        const videoQ = document.querySelector("video");
        videoQ.play().catch(() => {
          document.addEventListener(
            "click",
            () => {
              videoQ.play();
            },
            { once: true }
          );
        });
        const tempCanvas = document.createElement("canvas");
        tempCanvas.width = videoQ.videoWidth;
        tempCanvas.height = videoQ.videoHeight;
        const tempCtx = tempCanvas.getContext("2d");
        tempCtx.drawImage(videoQ, 0, 0, tempCanvas.width, tempCanvas.height);
        resolve(tempCanvas.toDataURL("image/jpeg"));
      };
    });

    // Helper to load an image and wait until it is fully loaded.
    const loadImage = (src) =>
      new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = src;
      });

    // Wait for all images (camera feeds, kiosk feed, and watermark) to be loaded.
    try {
      const [camerasImg, kioskCamImg, watermarkImg] = await Promise.all([
        loadImage(cameraUrl),
        loadImage(kioskCamData),
        loadImage(Watermark),
      ]);

      // Draw the images onto the canvas.
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");

      // Set canvas dimensions based on desired output (three images side by side).
      canvas.width = 1920;
      canvas.height = 3888;

      // draw water mark image on the canvas as the background and then draw the images on top of it
      ctx.drawImage(watermarkImg, 0, 0, canvas.width, canvas.height);
      // camera one kiosk camera on top middle
      ctx.drawImage(kioskCamImg, 150, 645, 1619, 934);

      // KIOSK 1: bottom left and b
      // Draw bottom left 1080p (from source: x=0, y=1080, width=1920, height=1080)
      // Crop bottom left section from the 1080p image
      ctx.drawImage(
        camerasImg, // source image
        0,
        598,
        838,
        482, // source rectangle: (x, y, width, height)
        150,
        1723,
        1619,
        934 // destination rectangle on the canvas
      );

      // Crop bottom right section from the 1080p image
      ctx.drawImage(
        camerasImg, // source image
        1082,
        598,
        838,
        482, // source rectangle: (x, y, width, height)
        150,
        2808,
        1619,
        934 // destination rectangle on the canvas
      );

      /* KIOSK 2: top left and top right 

      // Crop top left section from the 1080p image
      ctx.drawImage(
        camerasImg, // source image
        0,
        0,
        838,
        482, // source rectangle: (x, y, width, height)
        150,
        1723,
        1619,
        934 // destination rectangle on the canvas
      );

      // Crop top right section from the 1080p image
      ctx.drawImage(
        camerasImg, // source image
        1082,
        0,
        838,
        482, // source rectangle: (x, y, width, height)
        150,
        2808,
        1619,
        934 // destination rectangle on the canvas
      );
      */

      // Save the resulting selfie.
      setSelfie(canvas.toDataURL("image/jpeg"));
    } catch (error) {
      console.error("Error loading one of the images:", error);
    }
  };

  // --- PHONE VIEW ---
  const phoneView = (
    <div
      style={{
        backgroundColor: "black",
        color: "white",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        fontFamily: "Arial, sans-serif",
        fontSize: "1.5rem",
        fontWeight: "bold",
        position: "relative",
        zIndex: 3,
      }}
    >
      <img src={UpArrow} alt="Arrow pointing up" style={{ width: "85px" }} />
      <p>Look up at the screen...</p>
    </div>
  );

  // --- CAMERA VIEW OVERLAY ---
  // This overlay will show the selfie review phase.
  const cameraOverlay = (
    <div
      style={{
        position: "relative",
        zIndex: 2,
        textAlign: "center",
        padding: "2rem",
      }}
    >
      {!showBlackScreen ? (
        <div style={{ marginTop: "0.25rem" }}>
          {selfiePhoto ? (
            <>
              <img
                src={selfiePhoto}
                alt="Selfie"
                style={{
                  width: "86%",
                  height: "86%",
                  border: "1px solid white",
                  marginTop: "1rem",
                  marginLeft: "auto",
                  marginRight: "auto",
                  display: "block",
                  marginBottom: "1rem",
                }}
              />
              <div>
                <button
                  onClick={async () => {
                    if (selfiePhoto && navigator.share) {
                      try {
                        const response = await fetch(selfiePhoto);
                        const blob = await response.blob();
                        const file = new File([blob], "selfie.jpg", {
                          type: blob.type,
                        });

                        await navigator.share({
                          title: "In theaters April 11",
                          text: "In theaters April 11",
                          files: [file],
                        });

                        await new Promise((resolve) => setTimeout(resolve, 15000));

                        setShowBlackScreen(true);
                        setActionDone(true);
                      } catch (err) {
                        console.error("Error sharing", err);
                      }
                    } else {
                      alert("Share not supported on this device");
                    }
                  }}
                  style={{
                    background: "transparent",
                    border: "none",
                    padding: 0,
                    cursor: "pointer",
                    marginRight: "3.25rem",
                  }}
                >
                  <img
                    src={downloadIcon}
                    alt="Download Icon"
                    style={{ width: "50px", height: "50px" }}
                  />
                </button>

                <button
                  onClick={async () => {
                    if (selfiePhoto && navigator.share) {
                      try {
                        const response = await fetch(selfiePhoto);
                        const blob = await response.blob();
                        const file = new File([blob], "selfie.jpg", {
                          type: blob.type,
                        });

                        await navigator.share({
                          title: "In theaters April 11",
                          text: "In theaters April 11",
                          files: [file],
                        });

                        await new Promise((resolve) => setTimeout(resolve, 15000));

                        setShowBlackScreen(true);
                        setActionDone(true);
                      } catch (err) {
                        console.error("Error sharing", err);
                      }
                    } else {
                      alert("Share not supported on this device");
                    }
                  }}
                  style={{
                    background: "transparent",
                    border: "none",
                    padding: 0,
                    cursor: "pointer",
                    marginRight: "1rem",
                  }}
                >
                  <img
                    src={shareIcon}
                    alt="Share Icon"
                    style={{ width: "50px", height: "50px" }}
                  />
                </button>
              </div>
            </>
          ) : (
            <div
              style={{
                backgroundColor: "black",
                color: "white",
                minHeight: "100vh",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                fontFamily: "Arial, sans-serif",
                fontSize: "1.5rem",
                fontWeight: "bold",
                position: "relative",
                zIndex: 3,
              }}
            >
              <p>Creating photo...</p>
              <div
                style={{
                  width: "100%",
                  background: "#ccc",
                  borderRadius: "4px",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    width: `${progress}%`,
                    background: "#4caf50",
                    height: "30px",
                    textAlign: "center",
                    color: "white",
                    lineHeight: "30px",
                  }}
                ></div>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div
          style={{
            backgroundColor: "black",
            color: "white",
            minHeight: "100vh",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            fontFamily: "Arial, sans-serif",
            fontSize: "1.5rem",
            fontWeight: "bold",
            position: "relative",
            zIndex: 3,
          }}
        >
          <p>{message}</p>
        </div>
      )}
    </div>
  );

  // --- PRELOADED IFRAME CONTAINER ---
  // This container is always rendered so that the iframes are preloaded.
  // It will be visible only when currentView is "camera" and phase is "cameras".
  const preloadedIframes = (
    <div
      ref={preloadedContainerRef}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: currentView === "camera" && phase === "cameras" ? 1 : -1,
        opacity: currentView === "camera" && phase === "cameras" ? 1 : 0,
        transition: "opacity 0.5s",
        pointerEvents:
          currentView === "camera" && phase === "cameras" ? "auto" : "none",
      }}
    >
      <div
        style={{
          gap: "1rem",
        }}
      >
        {/* First camera feed: kiosk live stream */}
        <div className="video-wrapper">
          <div className="video-container">
            {kioskStream && (
              <div style={{ marginTop: "2rem" }}>
                <video
                  autoPlay
                  playsInline
                  muted
                  controls
                  style={{ width: "100%", height: "100%", border: "none" }}
                  ref={frontCamRef}
                ></video>
              </div>
            )}
          </div>
        </div>
        {/* Two Cameras Kiosk One */}
        {/* Second camera feed */}
        <div className="video-wrapper">
          <div className="video-container">
            <iframe
              title="bottom-left-camera"
              src="https://player.twitch.tv/?channel=dropthegrove&parent=sites.droppromo.com&muted=true&autoplay=true"
              width="854"
              height="480"
              style={{
                border: "none",
                position: "absolute",
                top: "-240px", // shift upward by one quadrant height
                left: "-5px", // no horizontal shift
              }}
              allow="autoplay; encrypted-media; muted;"
            ></iframe>
            {/* Transparent overlay */}
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                zIndex: 10, // ensure it's on top of the iframe
                backgroundColor: "transparent",
              }}
            ></div>
          </div>
        </div>
        {/* Third camera feed */}
        <div className="video-wrapper">
          <div className="video-container">
            <iframe
              title="bottom-right-camera"
              src="https://player.twitch.tv/?channel=dropthegrove&parent=sites.droppromo.com&muted=true&autoplay=true"
              width="854"
              height="480"
              style={{
                border: "none",
                position: "absolute",
                top: "-240px", // shift upward by one quadrant height
                left: "-425px", // shift left by one quadrant width
              }}
              allow="autoplay; encrypted-media; muted;"
            ></iframe>
            {/* Transparent overlay */}
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                zIndex: 10, // ensure it's on top of the iframe
                backgroundColor: "transparent",
              }}
            ></div>
          </div>
        </div>
        {/* Two Kiosk camera feeds */}
        {/* Second camera feed 
        <div className="video-wrapper">
          <div className="video-container">
            <iframe
              title="top-left-camera"
              src="https://player.twitch.tv/?channel=dropthegrove&parent=phone.droppromo.com&muted=true&autoplay=true"
              width="854"
              height="480"
              style={{
                border: "none",
                position: "absolute",
                top: "0px", // shift upward by one quadrant height
                left: "-5px", // no horizontal shift
              }}
              allow="autoplay; encrypted-media; muted;"
            ></iframe>
  <div
    style={{
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      zIndex: 10, // ensure it's on top of the iframe
      backgroundColor: "transparent",
    }}
  ></div>
          </div>
        </div>
        {/* Third camera feed //} 
        <div className="video-wrapper">
          <div className="video-container">
            <iframe
              title="bottom-right-camera"
              src="https://player.twitch.tv/?channel=dropthegrove&parent=phone.droppromo.com&muted=true&autoplay=true"
              width="854"
              height="480"
              style={{
                border: "none",
                position: "absolute",
                top: "0px", // shift upward by one quadrant height
                left: "-425px", // shift left by one quadrant width
              }}
              allow="autoplay; encrypted-media; muted;"
            ></iframe>
              <div
    style={{
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      zIndex: 10, // ensure it's on top of the iframe
      backgroundColor: "transparent",
    }}
  ></div>
          </div>
        </div>
        */}
      </div>
    </div>
  );

  // --- FINAL RENDER ---
  return (
    <div
      style={{
        position: "relative",
        backgroundColor: "black",
        minHeight: "100vh",
        color: "white",
      }}
    >
      {/* Always render the preloaded iframes (they will be hidden or visible based on currentView/phase) */}
      {preloadedIframes}
      {/* Render overlay based on currentView */}
      {currentView === "phone" ? phoneView : null}
      {currentView === "camera" &&
        (phase === "cameras"
          ? null
          : phase === "selfie"
          ? cameraOverlay
          : null)}

      <canvas ref={canvasRef} style={{ display: "none" }}></canvas>
    </div>
  );
};

export default PhonePage;
